import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

const PublicAmbassadorDashboard: React.FC = () => {
  const { campaignSlug, ambassadorSlug } = useParams<{ campaignSlug: string; ambassadorSlug: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!campaignSlug || !ambassadorSlug) return;
    axios.get(`${API_BASE}/public/ambassador-dashboard/${campaignSlug}/${ambassadorSlug}`)
      .then(res => setData(res.data))
      .catch(() => setError('לא נמצא שגריר'))
      .finally(() => setLoading(false));
  }, [campaignSlug, ambassadorSlug]);

  if (loading) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Heebo', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTop: '4px solid #f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#6b7280' }}>טוען דאשבורד...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error || !data) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Heebo', sans-serif" }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>😔</div>
        <p style={{ fontSize: 18, color: '#374151' }}>{error || 'שגיאה'}</p>
      </div>
    </div>
  );

  const { ambassador, campaign, orgName, shareUrl, donations, group } = data;
  const pct = ambassador.goal > 0 ? Math.min(100, Math.round((ambassador.totalRaised / ambassador.goal) * 100)) : 0;
  const groupPct = group && group.goal > 0 ? Math.min(100, Math.round((group.raised / group.goal) * 100)) : 0;
  const primary = '#f59e0b';

  return (
    <div dir="rtl" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #fffbeb 0%, #f8fafc 40%)', fontFamily: "'Heebo', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
      `}</style>

      <div style={{ maxWidth: 500, margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
          borderRadius: 20, padding: '28px 24px', textAlign: 'center', color: '#fff',
          boxShadow: '0 8px 32px rgba(245,158,11,0.3)', marginBottom: 20,
        }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🤝</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{ambassador.name}</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, opacity: 0.9 }}>שגריר/ה בקמפיין "{campaign.name}"</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.7 }}>{orgName}</p>
        </div>

        {/* Progress */}
        {ambassador.goal > 0 && (
          <div style={{
            background: '#fff', borderRadius: 16, padding: 24, marginBottom: 16,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, fontWeight: 700 }}>
              <span style={{ color: primary }}>₪{ambassador.totalRaised.toLocaleString('he-IL')}</span>
              <span style={{ color: '#6b7280' }}>מתוך ₪{ambassador.goal.toLocaleString('he-IL')}</span>
            </div>
            <div style={{ width: '100%', height: 20, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                background: `linear-gradient(90deg, #f59e0b, #f97316)`,
                borderRadius: 999, transition: 'width 1s ease-out',
              }} />
            </div>
            <p style={{ textAlign: 'center', marginTop: 8, fontSize: 20, fontWeight: 900, color: primary }}>{pct}%</p>
          </div>
        )}

        {/* The ambassador's group ("הקבוצה שלך") — team progress as a shared
            motivator: the sum every member is pushing together. */}
        {group && (
          <div style={{
            background: '#fff', borderRadius: 16, padding: 18, marginBottom: 16,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #ede9fe',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {group.imageUrl
                ? <img src={group.imageUrl} alt={group.name} style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover' }} />
                : <span style={{ width: 40, height: 40, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎯</span>}
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#374151' }}>הקבוצה שלך: {group.name}</p>
                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>{group.membersCount} שגרירים מגייסים יחד</p>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, fontWeight: 700 }}>
              <span style={{ color: '#7c3aed' }}>₪{Number(group.raised).toLocaleString('he-IL')}</span>
              {group.goal > 0 && <span style={{ color: '#6b7280' }}>מתוך ₪{Number(group.goal).toLocaleString('he-IL')}</span>}
            </div>
            <div style={{ width: '100%', height: 14, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                width: `${groupPct}%`, height: '100%',
                background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                borderRadius: 999, transition: 'width 1s ease-out',
              }} />
            </div>
            {group.goal > 0 && (
              <p style={{ textAlign: 'center', margin: '6px 0 0', fontSize: 15, fontWeight: 900, color: '#7c3aed' }}>{groupPct}%</p>
            )}
          </div>
        )}

        {/* Stats — 2×2 grid: raised / donors on top, link views / conversion below */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#059669' }}>₪{ambassador.totalRaised.toLocaleString('he-IL')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>סה"כ גויס</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#2563eb' }}>{ambassador.donorCount}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>תורמים</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#7c3aed' }}>{(ambassador.visitCount ?? 0).toLocaleString('he-IL')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>👁️ צפיות בלינק</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '18px 14px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: '#f59e0b' }}>{ambassador.conversionRate ?? 0}%</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>אחוז המרה</div>
          </div>
        </div>

        {/* Share link */}
        <div style={{
          background: '#fff', borderRadius: 14, padding: 18, marginBottom: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6',
        }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10 }}>🔗 הלינק שלי לשיתוף</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input readOnly value={shareUrl} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12, direction: 'ltr', color: '#6b7280' }} />
            <button
              onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: copied ? '#22c55e' : '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >{copied ? '✅ הועתק!' : '📋 העתק'}</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <a href={`https://wa.me/?text=${encodeURIComponent(`בואו תתרמו! ${shareUrl}`)}`} target="_blank" rel="noreferrer"
              style={{ flex: 1, padding: '10px', borderRadius: 10, background: '#25D366', color: '#fff', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}>
              📱 שתף בוואטסאפ
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
              style={{ flex: 1, padding: '10px', borderRadius: 10, background: '#1877F2', color: '#fff', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}>
              📘 שתף בפייסבוק
            </a>
          </div>
        </div>

        {/* Donations list */}
        <div style={{
          background: '#fff', borderRadius: 14, padding: 18,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6',
        }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>💝 התרומות שהבאתי</p>
          {donations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
              <p style={{ fontSize: 14 }}>עדיין אין תרומות</p>
              <p style={{ fontSize: 12 }}>שתפ/י את הלינק שלך כדי להתחיל לגייס!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {donations.map((d: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f9fafb', borderRadius: 10 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{d.name}</span>
                    <span style={{ fontSize: 12, color: '#9ca3af', marginRight: 8 }}>{new Date(d.date).toLocaleDateString('he-IL')}</span>
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#059669' }}>₪{d.amount.toLocaleString('he-IL')}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '24px 0 12px', color: '#9ca3af', fontSize: 11 }}>
          <p>מופעל ע"י <strong style={{ color: '#6b7280' }}>Amutech</strong> — משקיעים באנשים שעושים טוב</p>
        </div>
      </div>
    </div>
  );
};

export default PublicAmbassadorDashboard;
